import { t, flow, Instance } from 'mobx-state-tree';
import type {
  ContentType,
  FamilyUserResponse,
  MemberBlockResponse,
  MemberRoleAssignmentResponse,
  ContentRoleAssignmentResponse
} from 'we-tree-api-client/v2';
import { familiesApi } from '@/api/familiesWrapper';
import { domainStore } from '@/stores/DomainStore';
import { memberRolesApi } from '@/api/memberRolesWrapper';
import { memberBlocksApi } from '@/api/memberBlocksWrapper';
import { contentRolesApi } from '@/api/contentRolesWrapper';
import type {
  CreateContentRolePayload,
  CreateMemberBlockPayload,
  CreateMemberRoleOverridePayload,
  UpdateContentRolePayload,
  UpdateMemberBlockPayload,
  UpdateMemberRoleOverridePayload
} from '@/types/member-access';

function throwApiError(result: { success: false; error: { getUserMessage: () => string } }): never {
  throw new Error(result.error.getUserMessage() || 'Request failed');
}

/**
 * Orchestrates member role overrides, member blocks, and content role assignments.
 */
export const MemberAccessStore = t
  .model('MemberAccessStore', {
    familyId: t.maybeNull(t.string),
    familyMemberId: t.maybeNull(t.string),
    contentType: t.maybeNull(t.string),
    contentId: t.maybeNull(t.string),
    isLoading: t.optional(t.boolean, false),
    isSaving: t.optional(t.boolean, false),
    error: t.maybeNull(t.string),
    familyUsers: t.optional(t.frozen<FamilyUserResponse[]>(), []),
    roleAssignments: t.optional(t.frozen<MemberRoleAssignmentResponse[]>(), []),
    blocks: t.optional(t.frozen<MemberBlockResponse[]>(), []),
    contentAssignments: t.optional(t.frozen<ContentRoleAssignmentResponse[]>(), [])
  })
  .views((self) => {
    /** Assignable users: active members of this family, never the acting user themselves. */
    function selectable(taken: Set<string>): FamilyUserResponse[] {
      const actorUserId = domainStore.user?.id;
      return self.familyUsers.filter((u) => u.status === 'active' && u.userId !== actorUserId && !taken.has(u.userId));
    }

    return {
      userLabel(userId: string): string {
        const user = self.familyUsers.find((u) => u.userId === userId);
        if (!user) return 'User';
        return user.memberName || user.nickname || user.email || 'User';
      },
      availableUsersForRoles(): FamilyUserResponse[] {
        return selectable(new Set(self.roleAssignments.map((a) => a.userId)));
      },
      availableUsersForBlocks(): FamilyUserResponse[] {
        return selectable(new Set(self.blocks.map((b) => b.userId)));
      },
      availableUsersForContent(): FamilyUserResponse[] {
        return selectable(new Set(self.contentAssignments.map((a) => a.userId)));
      }
    };
  })
  .actions((self) => {
    const loadFamilyUsers = flow(function* (familyId: string) {
      const result = yield familiesApi.getFamilyUsers(familyId);
      if (!result.success) {
        throwApiError(result);
      }
      const active = (result.data.users || []).filter((u: FamilyUserResponse) => u.status === 'active');
      // Older API builds could return a user once per family they belong to; keep one entry each.
      const byUserId = new Map<string, FamilyUserResponse>();
      active.forEach((u: FamilyUserResponse) => {
        if (!byUserId.has(u.userId)) byUserId.set(u.userId, u);
      });
      self.familyUsers = Array.from(byUserId.values());
    });

    return {
      clearError() {
        self.error = null;
      },

      resetMemberTarget() {
        self.familyId = null;
        self.familyMemberId = null;
        self.roleAssignments = [];
        self.blocks = [];
        self.error = null;
        self.isLoading = false;
        self.isSaving = false;
      },

      resetContentTarget() {
        self.familyId = null;
        self.contentType = null;
        self.contentId = null;
        self.contentAssignments = [];
        self.error = null;
        self.isLoading = false;
        self.isSaving = false;
      },

      loadFamilyUsers,

      loadMemberAccess: flow(function* (familyId: string, familyMemberId: string) {
        self.isLoading = true;
        self.error = null;
        self.familyId = familyId;
        self.familyMemberId = familyMemberId;
        try {
          yield loadFamilyUsers(familyId);
          const [rolesResult, blocksResult] = yield Promise.all([
            memberRolesApi.list(familyId, familyMemberId),
            memberBlocksApi.list(familyId, familyMemberId)
          ]);
          if (!rolesResult.success) throwApiError(rolesResult);
          if (!blocksResult.success) throwApiError(blocksResult);
          self.roleAssignments = rolesResult.data.assignments || [];
          self.blocks = blocksResult.data.blocks || [];
        } catch (err) {
          self.error = err instanceof Error ? err.message : 'Failed to load access settings';
          self.roleAssignments = [];
          self.blocks = [];
        } finally {
          self.isLoading = false;
        }
      }),

      createMemberRoleOverride: flow(function* (payload: CreateMemberRoleOverridePayload) {
        if (!self.familyId || !self.familyMemberId) {
          throw new Error('No member selected');
        }
        self.isSaving = true;
        self.error = null;
        try {
          const result = yield memberRolesApi.create(self.familyId, self.familyMemberId, {
            familyId: self.familyId,
            familyMemberId: self.familyMemberId,
            userId: payload.userId,
            role: payload.role,
            includeDescendants: false
          });
          if (!result.success) throwApiError(result);
          self.roleAssignments = [...self.roleAssignments, result.data];
          return result.data as MemberRoleAssignmentResponse;
        } catch (err) {
          self.error = err instanceof Error ? err.message : 'Failed to create role override';
          throw err;
        } finally {
          self.isSaving = false;
        }
      }),

      updateMemberRoleOverride: flow(function* (userId: string, payload: UpdateMemberRoleOverridePayload) {
        if (!self.familyId || !self.familyMemberId) {
          throw new Error('No member selected');
        }
        self.isSaving = true;
        self.error = null;
        try {
          const result = yield memberRolesApi.update(self.familyId, self.familyMemberId, userId, {
            role: payload.role
          });
          if (!result.success) throwApiError(result);
          self.roleAssignments = self.roleAssignments.map((a) => (a.userId === userId ? result.data : a));
          return result.data as MemberRoleAssignmentResponse;
        } catch (err) {
          self.error = err instanceof Error ? err.message : 'Failed to update role override';
          throw err;
        } finally {
          self.isSaving = false;
        }
      }),

      deleteMemberRoleOverride: flow(function* (userId: string) {
        if (!self.familyId || !self.familyMemberId) {
          throw new Error('No member selected');
        }
        self.isSaving = true;
        self.error = null;
        try {
          const result = yield memberRolesApi.remove(self.familyId, self.familyMemberId, userId);
          if (!result.success) throwApiError(result);
          self.roleAssignments = self.roleAssignments.filter((a) => a.userId !== userId);
        } catch (err) {
          self.error = err instanceof Error ? err.message : 'Failed to remove role override';
          throw err;
        } finally {
          self.isSaving = false;
        }
      }),

      createMemberBlock: flow(function* (payload: CreateMemberBlockPayload) {
        if (!self.familyId || !self.familyMemberId) {
          throw new Error('No member selected');
        }
        self.isSaving = true;
        self.error = null;
        try {
          const result = yield memberBlocksApi.create(self.familyId, self.familyMemberId, {
            familyId: self.familyId,
            familyMemberId: self.familyMemberId,
            userId: payload.userId,
            includeDescendants: payload.includeDescendants ?? false,
            reason: payload.reason?.trim() || undefined
          });
          if (!result.success) {
            const err = result.error;
            self.error = err.getUserMessage() || 'Failed to create block';
            throw err;
          }
          self.blocks = [...self.blocks, result.data];
          return result.data as MemberBlockResponse;
        } catch (err) {
          if (!(err as { getUserMessage?: unknown })?.getUserMessage) {
            self.error = err instanceof Error ? err.message : 'Failed to create block';
          }
          throw err;
        } finally {
          self.isSaving = false;
        }
      }),

      updateMemberBlock: flow(function* (userId: string, payload: UpdateMemberBlockPayload) {
        if (!self.familyId || !self.familyMemberId) {
          throw new Error('No member selected');
        }
        self.isSaving = true;
        self.error = null;
        try {
          const result = yield memberBlocksApi.update(self.familyId, self.familyMemberId, userId, {
            includeDescendants: payload.includeDescendants,
            reason: payload.reason
          });
          if (!result.success) throwApiError(result);
          self.blocks = self.blocks.map((b) => (b.userId === userId ? result.data : b));
          return result.data as MemberBlockResponse;
        } catch (err) {
          self.error = err instanceof Error ? err.message : 'Failed to update block';
          throw err;
        } finally {
          self.isSaving = false;
        }
      }),

      deleteMemberBlock: flow(function* (userId: string) {
        if (!self.familyId || !self.familyMemberId) {
          throw new Error('No member selected');
        }
        self.isSaving = true;
        self.error = null;
        try {
          const result = yield memberBlocksApi.remove(self.familyId, self.familyMemberId, userId);
          if (!result.success) throwApiError(result);
          self.blocks = self.blocks.filter((b) => b.userId !== userId);
        } catch (err) {
          self.error = err instanceof Error ? err.message : 'Failed to remove block';
          throw err;
        } finally {
          self.isSaving = false;
        }
      }),

      loadContentAccess: flow(function* (familyId: string, contentType: ContentType | string, contentId: string) {
        self.isLoading = true;
        self.error = null;
        self.familyId = familyId;
        self.contentType = contentType;
        self.contentId = contentId;
        try {
          yield loadFamilyUsers(familyId);
          const result = yield contentRolesApi.list(familyId, contentType, contentId);
          if (!result.success) throwApiError(result);
          self.contentAssignments = result.data.assignments || [];
        } catch (err) {
          self.error = err instanceof Error ? err.message : 'Failed to load content permissions';
          self.contentAssignments = [];
        } finally {
          self.isLoading = false;
        }
      }),

      createContentRole: flow(function* (payload: CreateContentRolePayload) {
        if (!self.familyId || !self.contentType || !self.contentId) {
          throw new Error('No content selected');
        }
        self.isSaving = true;
        self.error = null;
        try {
          const result = yield contentRolesApi.create(self.familyId, self.contentType, self.contentId, {
            familyId: self.familyId,
            contentType: self.contentType as ContentType,
            contentId: self.contentId,
            userId: payload.userId,
            role: payload.role
          });
          if (!result.success) throwApiError(result);
          self.contentAssignments = [...self.contentAssignments, result.data];
          return result.data as ContentRoleAssignmentResponse;
        } catch (err) {
          self.error = err instanceof Error ? err.message : 'Failed to create content permission';
          throw err;
        } finally {
          self.isSaving = false;
        }
      }),

      updateContentRole: flow(function* (userId: string, payload: UpdateContentRolePayload) {
        if (!self.familyId || !self.contentType || !self.contentId) {
          throw new Error('No content selected');
        }
        self.isSaving = true;
        self.error = null;
        try {
          const result = yield contentRolesApi.update(self.familyId, self.contentType, self.contentId, userId, {
            role: payload.role
          });
          if (!result.success) throwApiError(result);
          self.contentAssignments = self.contentAssignments.map((a) => (a.userId === userId ? result.data : a));
          return result.data as ContentRoleAssignmentResponse;
        } catch (err) {
          self.error = err instanceof Error ? err.message : 'Failed to update content permission';
          throw err;
        } finally {
          self.isSaving = false;
        }
      }),

      deleteContentRole: flow(function* (userId: string) {
        if (!self.familyId || !self.contentType || !self.contentId) {
          throw new Error('No content selected');
        }
        self.isSaving = true;
        self.error = null;
        try {
          const result = yield contentRolesApi.remove(self.familyId, self.contentType, self.contentId, userId);
          if (!result.success) throwApiError(result);
          self.contentAssignments = self.contentAssignments.filter((a) => a.userId !== userId);
        } catch (err) {
          self.error = err instanceof Error ? err.message : 'Failed to remove content permission';
          throw err;
        } finally {
          self.isSaving = false;
        }
      })
    };
  });

export type MemberAccessStoreType = Instance<typeof MemberAccessStore>;

export const memberAccessStore = MemberAccessStore.create({});
